import { formatDateTime } from "@/lib/display-labels";
import type { TrackRecordLine, TrackRecordScope } from "@/types/track-record.types";
import { pct, testedLabel } from "./format";
import { TrackRecordMetricRows } from "./TrackRecordMetricRows";

interface TrackRecordByLineProps {
  lines: readonly TrackRecordLine[];
}

// Spec 079 (US1) — a abertura por LINHA e, dentro dela, por PRODUTO.
//
// A camada existe porque é ela que muda a ação: "o gêmeo acerta 62%" não diz o que fazer, mas "o
// gêmeo acerta 92% na Linha A e 31% na Linha B" manda alguém para a Linha B. É a mesma razão pela
// qual a régua é uma cascata e não um número só.
//
// ⚠️ A linha SEM produto listado tem MAIS DE UMA causa, e afirmar só a mais comum é pior que não
// dizer nada: a linha recém-zerada por um conserto também chega aqui com a lista vazia, e dizer a
// ela "os desta linha misturaram produtos" é uma afirmação falsa sobre o chão de fábrica.

/** O par acurácia + amostra, na forma curta que cabe ao lado de um nome. */
function ScopeBadge({ scope }: { scope: TrackRecordScope }) {
  return (
    <span className="flex shrink-0 items-baseline gap-2">
      <span className="font-mono text-[15px] tabular-nums text-text-primary">
        {pct(scope.accuracy)}
      </span>
      <span className="text-[11px] text-text-tertiary">{testedLabel(scope.sampleSize)}</span>
    </span>
  );
}

/**
 * Por que esta linha não tem régua por produto (FR-013/FR-025).
 *
 * Três estados distintos chegam aqui com `products` vazio, e cada um pede uma ação diferente —
 * esperar evidência nova, olhar as ordens, ou ir julgar a fila. Um texto único para os três
 * transforma dois deles em desinformação.
 */
function noProductsReason(line: TrackRecordLine): string {
  if (line.sampleSize === 0 && line.zeroedAt) {
    return "a régua desta linha foi zerada pelo último conserto do modelo — ainda não há compromisso apurado depois dele.";
  }
  if (line.sampleSize === 0) {
    return "nenhum compromisso desta linha foi testado ainda — os apurados seguem aguardando julgamento ou não seguiram o combinado.";
  }
  return "nenhum compromisso desta linha teve produto único — os apurados misturaram produtos, e ratear o erro entre eles seria inventar uma medida.";
}

export function TrackRecordByLine({ lines }: TrackRecordByLineProps) {
  return (
    <ul className="space-y-3">
      {lines.map((line) => (
        <li key={line.lineId} className="rounded-md border border-border-subtle">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border-subtle p-3">
            <div className="min-w-0">
              <h3 className="text-[13px] font-medium text-text-primary">{line.lineName}</h3>
              {/* O MARCO ZERO ao lado do nome (FR-025). Sem ele, "régua nova desde ontem" e "linha
                  que nunca apurou nada" desenham idêntico — e as duas pedem ações opostas. */}
              {line.zeroedAt ? (
                <p className="text-[11px] leading-tight text-text-tertiary">
                  régua nova desde {formatDateTime(line.zeroedAt)} — o histórico anterior ao
                  conserto do modelo não conta
                </p>
              ) : null}
            </div>
            <ScopeBadge scope={line} />
          </div>

          <div className="space-y-3 p-3">
            <TrackRecordMetricRows scope={line} />

            {line.products.length > 0 ? (
              <div className="space-y-1.5">
                <h4 className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-secondary">
                  Por produto
                </h4>
                <ul className="space-y-1.5">
                  {line.products.map((product) => (
                    <li
                      key={product.productId}
                      className="flex flex-wrap items-baseline justify-between gap-3 rounded-md bg-bg-muted-rd px-3 py-2"
                    >
                      <span className="text-[13px] text-text-primary">{product.productName}</span>
                      <ScopeBadge scope={product} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              // Estado explicado, não omitido: a ausência de produtos tem uma causa concreta, e sem
              // a frase ela se lê como dado faltando. Parágrafo e não `Pill`: o componente é
              // `h-[22px]` + `whitespace-nowrap`, e uma frase inteira dentro dele estoura o card.
              <p className="text-[11px] leading-tight text-text-secondary">
                Sem régua por produto — {noProductsReason(line)}
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
